import { useMemo, useState } from "react"
import { Trash2 } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Select } from "@/components/ui/select"
import { HttpMethodBadge } from "./http-method-badge"
import { ErrorRateSparkline } from "./error-rate-sparkline"
import { fmtCompactRelative } from "@/lib/format"
import { cn } from "@/lib/cn"
import type { Endpoint, HttpMethod } from "@/types/endpoint"

type Props = {
  endpoints: Endpoint[]
  selectedId: number | null
  onSelect: (id: number) => void
  onChartClick: (endpoint: Endpoint) => void
  /** Per-row trash icon. When omitted, the column is hidden — keeps the
      list usable in read-only contexts. */
  onDelete?: (endpoint: Endpoint) => void
}

type SortKey = "lastHit" | "errorRateDesc" | "nameAsc"
type MethodFilter = "ALL" | HttpMethod

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "lastHit",       label: "Last hit" },
  { value: "errorRateDesc", label: "Error rate (30D)" },
  { value: "nameAsc",       label: "Name A→Z" },
]

const METHOD_OPTIONS: { value: MethodFilter; label: string }[] = [
  { value: "ALL",    label: "All methods" },
  { value: "GET",    label: "GET" },
  { value: "POST",   label: "POST" },
  { value: "PUT",    label: "PUT" },
  { value: "PATCH",  label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
]

// Catalog column for the /endpoints page. Header (search + sort + method
// filter), then the table. Selection lifts the row into the right-side
// editor. Theme matches /topics + /escalation_reasons:
//   compact rows, thin red outline on selection, count + sort strip.
export function EndpointList({ endpoints, selectedId, onSelect, onChartClick, onDelete }: Props) {
  const [search, setSearch] = useState("")
  const [method, setMethod] = useState<MethodFilter>("ALL")
  const [sort, setSort] = useState<SortKey>("lastHit")

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return endpoints.filter((e) => {
      if (method !== "ALL" && e.httpMethod !== method) return false
      if (!q) return true
      return (
        e.name.toLowerCase().includes(q) ||
        e.key.toLowerCase().includes(q) ||
        e.pathTemplate.toLowerCase().includes(q)
      )
    })
  }, [endpoints, search, method])

  const sorted = useMemo(() => {
    const rows = [...filtered]
    rows.sort((a, b) => {
      if (sort === "nameAsc") return a.name.localeCompare(b.name)
      if (sort === "errorRateDesc") {
        const aErr = avgErrorPct(a.errorPctByDay)
        const bErr = avgErrorPct(b.errorPctByDay)
        return bErr - aErr
      }
      // lastHit (default): null → bottom; recency desc otherwise
      const av = a.lastHitAt ? Date.parse(a.lastHitAt) : 0
      const bv = b.lastHitAt ? Date.parse(b.lastHitAt) : 0
      return bv - av
    })
    return rows
  }, [filtered, sort])

  return (
    <Card className="flex h-full min-h-0 flex-col p-0">
      <header className="space-y-3 border-b border-border p-4">
        <div>
          <h2 className="text-base font-semibold text-app-text">Endpoint catalog</h2>
          <p className="mt-0.5 text-sm text-app-muted">
            Browse, search, and test your company's API endpoints.
          </p>
        </div>

        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search endpoints…"
          className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none"
        />

        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide text-app-muted">Method</span>
          <div className="min-w-[160px]">
            <Select<MethodFilter>
              value={METHOD_OPTIONS.find((o) => o.value === method) ?? METHOD_OPTIONS[0]}
              options={METHOD_OPTIONS}
              onChange={(o) => o && setMethod(o.value)}
              isSearchable={false}
            />
          </div>
        </div>
      </header>

      <div className="flex items-center justify-between border-b border-border bg-app-panel2/30 px-4 py-2 text-[11px] text-app-muted">
        <div>{sorted.length} endpoint{sorted.length === 1 ? "" : "s"}</div>
        <div className="min-w-[200px]">
          <Select<SortKey>
            value={SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0]}
            options={SORT_OPTIONS}
            onChange={(o) => o && setSort(o.value)}
            isSearchable={false}
          />
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-app-muted">
          {endpoints.length === 0 ? "No endpoints yet — click + New endpoint to add one." : "No matches."}
        </div>
      ) : (
        <div className="min-h-0 flex-1 lg:overflow-y-auto">
          {/* Column header is desktop-only; mobile cards self-label. */}
          <div className="hidden md:block">
            <ColumnHeader />
          </div>
          {sorted.map((e) => (
            <EndpointRow
              key={e.id}
              endpoint={e}
              selected={e.id === selectedId}
              onClick={() => onSelect(e.id)}
              onChartClick={() => onChartClick(e)}
              onDelete={onDelete ? () => onDelete(e) : undefined}
            />
          ))}
        </div>
      )}
    </Card>
  )
}

// Desktop row grid: method | name | sparkline | last-hit | trash. On
// mobile we stack — the sparkline + last-hit become a footer row under
// the name, and the method badge sits inline with the name.
const ROW_GRID =
  "md:grid md:grid-cols-[64px_minmax(0,1.4fr)_140px_88px_32px] md:items-center md:gap-3"

function ColumnHeader() {
  return (
    <div
      className={cn(
        ROW_GRID,
        "border-b border-border bg-app-panel2/20 px-4 py-2 text-[11px] uppercase tracking-wide text-app-muted",
      )}
    >
      <div>Method</div>
      <div>Name</div>
      <div>Error rate (30D)</div>
      <div className="text-right">Last hit</div>
      <div className="sr-only">Delete</div>
    </div>
  )
}

function EndpointRow({
  endpoint,
  selected,
  onClick,
  onChartClick,
  onDelete,
}: {
  endpoint: Endpoint
  selected: boolean
  onClick: () => void
  onChartClick: () => void
  onDelete?: () => void
}) {
  const referenced = (endpoint.referenceCount ?? 0) > 0
  const deleteDisabled = !onDelete || referenced
  const deleteTitle = !onDelete
    ? undefined
    : referenced
      ? `Referenced by ${endpoint.referenceCount} skill(s) — detach to enable delete`
      : "Delete endpoint"
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onClick() } }}
      className={cn(
        ROW_GRID,
        "cursor-pointer border border-transparent border-b border-b-border/60 px-4 py-3 outline-none transition last:border-b-0",
        selected
          ? "rounded-control border-brand-red bg-brand-redSoft/20 shadow-[inset_0_0_0_1px_rgb(220_38_38)]"
          : "hover:bg-white/[0.02] focus-visible:bg-white/[0.04]",
      )}
    >
      {/* Mobile: method + name share a row, then key/path, then sparkline + last-hit footer.
          Desktop: ROW_GRID lays out 4 columns. */}
      <div className="flex items-center gap-2 md:block">
        <HttpMethodBadge method={endpoint.httpMethod} />
      </div>
      <div className="min-w-0 mt-2 md:mt-0">
        <div className="flex items-center gap-2">
          {endpoint.last24hErrorCount > 0 && (
            <span title={`${endpoint.last24hErrorCount} error(s) in the last 24h`} className="text-status-error">⚠</span>
          )}
          <div className="truncate text-sm font-medium text-app-text">{endpoint.name}</div>
        </div>
        <div className={cn(
          "truncate font-mono text-[11px]",
          selected ? "text-brand-red" : "text-app-muted",
        )}>{endpoint.key}</div>
        <div className="mt-0.5 truncate font-mono text-xs text-app-muted">{endpoint.pathTemplate}</div>
      </div>
      <div
        onClick={(ev) => { ev.stopPropagation() }}
        className="mt-2 md:mt-0"
      >
        <ErrorRateSparkline
          errorPctByDay={endpoint.errorPctByDay}
          callsByDay={endpoint.callsByDay}
          onClick={onChartClick}
        />
      </div>
      <div className="mt-1 text-xs text-app-muted md:mt-0 md:text-right">
        <span className="md:hidden">Last hit: </span>
        {fmtCompactRelative(endpoint.lastHitAt)}
      </div>
      <div className="mt-2 flex justify-end md:mt-0">
        {onDelete && (
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation()
              if (!deleteDisabled) onDelete()
            }}
            disabled={deleteDisabled}
            title={deleteTitle}
            aria-label="Delete endpoint"
            className={cn(
              "grid h-7 w-7 place-items-center rounded-control border border-transparent text-app-muted transition",
              !deleteDisabled && "hover:border-status-error/40 hover:bg-status-error/10 hover:text-status-error",
              deleteDisabled && "opacity-40 cursor-not-allowed",
            )}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  )
}

function avgErrorPct(daily: number[] | undefined): number {
  if (!daily || daily.length === 0) return 0
  const sum = daily.reduce((a, b) => a + b, 0)
  return sum / daily.length
}
